/**
 * Utility functions for handling subscription data and status
 */

/**
 * Parse subscription data from API response and calculate current status
 * @param {Object} data - Subscription data from API
 * @returns {Object|null} - Processed subscription info or null if invalid data
 */
export const parseSubscriptionData = (data) => {
  // Verify required fields exist
  if (!data || data.subscriptionType === undefined) {
    console.log('No valid subscription data found, or essential fields missing.');
    return null;
  }

  // If type is 0 (Free), treat as no active plan
  if (data.subscriptionType === 0) {
    console.log('Subscription type is Free.');
    return null;
  }

  // Check subscription status
  let status = 'ACTIVE';
  
  // Check if subscription is cancelled
  if (data.isCancelled === true || data.status === 'CANCELLED') {
    status = 'CANCELLED';
  }

  // Map subscriptionType to plan KEY for paid plans
  let planKey = null; 
  switch (data.subscriptionType) {
    case 1: planKey = 'MONTHLY'; break;
    case 2: planKey = 'SIX_MONTH'; break;
    case 3: planKey = 'ANNUAL'; break;
    default:
      console.warn('Unknown non-free subscriptionType:', data.subscriptionType);
      return null;
  }

  // Return structured subscription information
  return {
    plan: planKey,
    expiresAt: data.expiresAt,
    status: status,
    subscriptionSource: data.subscriptionSource || 'UNKNOWN',
    subscriptionId: data.subscriptionId
  };
};

/**
 * Format a subscription plan for display
 * @param {string} planKey - Plan identifier (MONTHLY, SIX_MONTH, ANNUAL)
 * @returns {string} - Human-readable plan name
 */
export const getPlanDisplayName = (planKey) => {
  switch (planKey) {
    case 'MONTHLY': return 'Monthly Plan';
    case 'SIX_MONTH': return 'Semi-Annual Plan';
    case 'ANNUAL': return 'Annual Plan';
    default: return 'Unknown Plan';
  }
};

/**
 * Calculate days remaining until subscription expires
 * @param {string} expiresAt - Expiration date string
 * @returns {number} - Days remaining (0 if expired)
 */
export const calculateDaysRemaining = (expiresAt) => {
  if (!expiresAt) return 0;
  
  try {
    const expiryDate = new Date(expiresAt);
    const today = new Date();
    
    // Reset time parts to compare dates only
    today.setHours(0, 0, 0, 0);
    const expiryDateOnly = new Date(expiryDate);
    expiryDateOnly.setHours(0, 0, 0, 0);
    
    // Calculate difference in milliseconds
    const diffMs = expiryDateOnly - today;
    
    // Convert to days and round to handle daylight saving time issues
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    // Return 0 if negative (already expired)
    return Math.max(0, diffDays);
  } catch (error) {
    console.error('Error calculating days remaining:', error);
    return 0;
  }
};
